import is from '@sindresorhus/is';
import noop from 'lodash/noop';
import type { ReactNode } from 'react';
import { createContext, useEffect, useMemo, useRef, useState } from 'react';

import { useApplicationContext } from '~/shared/context/use-application-context';
import type { TrackingEvent } from '~/shared/tracking/tracking';

import type { MixpanelClient, MixpanelTrackingService } from './initialize-mixpanel-tracking';
import { initializeMixpanelTracking } from './initialize-mixpanel-tracking';

export type MixpanelTrackingContextValue = {
    readonly trackEvent: (event: TrackingEvent) => void;
};

export const MixpanelTrackingContext = createContext<MixpanelTrackingContextValue | undefined>(undefined);

type LoadMixpanelClient = () => Promise<MixpanelClient>;

const loadMixpanelClient: LoadMixpanelClient = async () => {
    const { default: client } = await import('mixpanel-browser');

    return client;
};

type MixpanelTrackingProviderProps = {
    readonly children: ReactNode;
    readonly loadClient?: LoadMixpanelClient;
};

export const MixpanelTrackingProvider = ({
    children,
    loadClient = loadMixpanelClient,
}: MixpanelTrackingProviderProps): ReactNode => {
    const { applicationState } = useApplicationContext();
    const { customization, languageKey, myUserProfile } = applicationState;

    const initializationPromiseRef = useRef<Promise<MixpanelTrackingService> | null>(null);
    const [service, setService] = useState<MixpanelTrackingService | null>(null);

    const mixpanelProjectId = customization.mapOr(undefined, (c) => c.commonConfiguration.mixpanelProjectId);
    const tenantId = customization.map((c) => c.tenantId).unwrapOr('');
    const isLocalhost = globalThis.location?.hostname === 'localhost';

    useEffect(() => {
        if (!is.null(service) || isLocalhost || !is.nonEmptyString(mixpanelProjectId) || myUserProfile.isNothing) {
            return;
        }

        initializationPromiseRef.current ??= loadClient().then((client) =>
            initializeMixpanelTracking(client, {
                languageKey,
                mixpanelProjectId,
                tenantId,
                user: myUserProfile.value,
            }),
        );

        let isActive = true;

        initializationPromiseRef.current
            .then((initializedService) => {
                if (isActive) {
                    setService(initializedService);
                }
            })
            .catch(() => {
                if (isActive) {
                    initializationPromiseRef.current = null;
                }
            });

        return () => {
            isActive = false;
        };
    }, [isLocalhost, languageKey, loadClient, mixpanelProjectId, myUserProfile, service, tenantId]);

    const value = useMemo<MixpanelTrackingContextValue>(() => {
        if (!is.null(service)) {
            return { trackEvent: service.sendTrackingEvent };
        }

        return { trackEvent: noop };
    }, [service]);

    return <MixpanelTrackingContext.Provider value={value}>{children}</MixpanelTrackingContext.Provider>;
};
